import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import type {
  MeetBackground,
  MeetBackgroundPreviewBindings,
} from "../meet-background.interface";
import { MeetBackgroundService } from "../meet-background.service";
import { CANVAS_DEFAULT_FONT_FAMILY } from "../../shared/font-presets";
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  takeWhile,
  tap,
} from "rxjs/operators";
import {
  MEET_LAYOUT,
  meetEmployeeRenderUrl,
  meetFallbackQrTarget,
  meetPreviewEmployeeName,
  meetPreviewJobTitle,
  meetPreviewLogoUrl,
} from "../meet-background.util";
import { forkJoin, merge, of, Subject, Subscription, timer } from "rxjs";
import { DigitalCardService } from "../../digital-cards/digital-card.service";
import type { EmployeeWithBrand } from "../../digital-cards/card-digital.interface";
import { CompanyService } from "../../company/company.service";
import type { EmployeePickerItem } from "../../employees/employees.interface";
import {
  canvasListMainPhase,
  type CanvasListMainPhase,
} from "../../shared/canvas-list-view";
import {
  DELETE_MEET_BACKGROUND_FAILURE,
  DELETE_MEET_BACKGROUND_SUCCESS,
  DeleteMeetBackgroundAction,
  DeleteMeetBackgroundSuccessAction,
  GetMeetBackgroundsAction,
} from "../meet-background.actions";
import {
  getMeetBackgroundList,
  getMeetBackgroundLoading,
} from "../meet-background.reducer";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import { ConfirmationDialogParams } from "../../shared/confirmation-dialog/confirmation-dialog.interface";

const POLLING_INTERVAL = 2000;
const RENDER_SYNC_MAX_POLLS = 60;

@Component({
  selector: "app-meet-background-list",
  templateUrl: "./meet-background-list.component.html",
  styleUrls: ["./meet-background-list.component.scss"],
  standalone: false,
})
export class MeetBackgroundListComponent implements OnInit, OnDestroy {
  loading = true;
  listLoading = true;
  private storeListLoading = true;
  private companyLoading = true;
  deleting = false;
  defaultSettingTemplateId: string | null = null;
  items: MeetBackground[] = [];
  selected: MeetBackground | null = null;
  selectedDetail: MeetBackground | null = null;
  loadingDetail = false;
  previewQrTarget: string | null = null;
  loadingPreviewQr = false;
  previewQrSyncing = false;
  previewEmployeeLoading = false;
  companyLogoUrl: string | null = null;
  companyFontFamily = "";
  companyFontFileUrl: string | null = null;
  previewEmployee: EmployeePickerItem | null = null;
  previewEmployeeData: EmployeeWithBrand | null = null;
  employeePickerReady = false;
  renderSyncing = false;
  private readonly previewEmail$ = new Subject<string>();
  private storeSub?: Subscription;
  private previewQrSub?: Subscription;
  private renderSyncSub?: Subscription;
  private previewQrRequestSeq = 0;
  private detailRequestSeq = 0;
  private renderSyncSeq = 0;

  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private dialog: MatDialog,
    private meetBackgroundService: MeetBackgroundService,
    private digitalCardService: DigitalCardService,
    private companyService: CompanyService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.storeSub = new Subscription();
    this.storeSub.add(
      this.store.select(getMeetBackgroundLoading).subscribe((isLoading) => {
        this.storeListLoading = isLoading;
        this.listLoading =
          this.storeListLoading ||
          (!!this.selected && !this.employeePickerReady);
        this.loading = this.listLoading || this.companyLoading;
      }),
    );
    this.storeSub.add(
      this.store.select(getMeetBackgroundList).subscribe((items) => {
        this.items = items ?? [];
        if (this.items.length === 0) {
          this.selected = null;
          this.selectedDetail = null;
          this.previewQrTarget = null;
          this.listLoading =
            this.storeListLoading || (!true && !this.employeePickerReady);
          this.loading = this.listLoading || this.companyLoading;
          return;
        }
        const selectedId = this.selected?.meetBackgroundId ?? "";
        const keep =
          selectedId.length > 0 &&
          this.items.some(
            (item) => (item.meetBackgroundId ?? "") === selectedId,
          );
        if (!keep) {
          this.select(this.items[0]);
          this.listLoading =
            this.storeListLoading ||
            (!!this.selected && !this.employeePickerReady);
          this.loading = this.listLoading || this.companyLoading;
          return;
        }
        const next =
          this.items.find(
            (item) => (item.meetBackgroundId ?? "") === selectedId,
          ) ?? this.items[0];
        this.selected = next;
        this.listLoading =
          this.storeListLoading ||
          (!!this.selected && !this.employeePickerReady);
        this.loading = this.listLoading || this.companyLoading;
        this.refreshPreviewQr(next);
        this.loadSelectedDetail(next.meetBackgroundId?.trim() ?? "");
      }),
    );
    this.storeSub.add(
      this.previewEmail$
        .pipe(
          tap(() => {
            this.previewEmployeeLoading = true;
            this.previewEmployeeData = null;
          }),
          switchMap((email) =>
            email
              ? this.digitalCardService.getPreviewEmployee(email).pipe(
                  map((response) => response.employee),
                  catchError(() => of<EmployeeWithBrand | null>(null)),
                )
              : of<EmployeeWithBrand | null>(null),
          ),
          tap((employee) => {
            this.previewEmployeeData = employee;
            this.previewEmployeeLoading = false;
          }),
        )
        .subscribe(),
    );
    this.companyService.get().subscribe({
      next: (company) => {
        this.companyLogoUrl = company?.photoUrl
          ? `${company.photoUrl}?t=${Date.now()}`
          : null;
        this.companyFontFamily =
          (company?.brandFontFamily ?? "").trim() ||
          (company?.brandFontFileName ?? "").trim().replace(/\.[^.]+$/, "") ||
          "";
        this.companyFontFileUrl =
          (company?.brandFontFileUrl ?? "").trim() || null;
        this.companyLoading = false;
        this.loading = this.listLoading || this.companyLoading;
      },
      error: () => {
        this.companyLoading = false;
        this.loading = this.listLoading || this.companyLoading;
      },
    });
    this.store.dispatch(new GetMeetBackgroundsAction());
  }

  get previewBindings(): MeetBackgroundPreviewBindings | null {
    const settings = this.selected?.settings;
    if (!settings) {
      return null;
    }
    const fontCssUrl = settings.fontCssUrl?.trim() || null;
    const storedFamily = settings.fontFamily?.trim() || "";
    const fontName = settings.fontName?.trim() || "";
    const familyPrimary = storedFamily
      .split(",")[0]
      ?.trim()
      .replace(/^["']|["']$/g, "");
    const hasExplicitFont = !!(fontCssUrl || storedFamily || fontName);
    const usesCompanyFont =
      !!this.companyFontFileUrl &&
      !!this.companyFontFamily &&
      !fontCssUrl &&
      (!hasExplicitFont ||
        familyPrimary.toLowerCase() === this.companyFontFamily.toLowerCase());
    const fontFamily = usesCompanyFont
      ? `"${this.companyFontFamily.replace(/"/g, "")}"`
      : storedFamily || CANVAS_DEFAULT_FONT_FAMILY;
    return {
      layoutVariant: settings.layoutVariant ?? "corner",
      barWidth: settings.barWidth ?? "compact",
      corner: settings.corner,
      logoCorner: settings.logoCorner ?? "top-left",
      showQrCaption:
        settings.showQrCaption === true || !!settings.qrCaption?.trim(),
      qrCaption: settings.qrCaption ?? "",
      showEmployeeName: settings.showEmployeeName === true,
      previewEmployeeName: meetPreviewEmployeeName(this.previewEmployeeData),
      showJobTitle: settings.showJobTitle === true,
      previewJobTitle: meetPreviewJobTitle(this.previewEmployeeData),
      showLogo: settings.showLogo === true,
      logoUrl: meetPreviewLogoUrl(
        settings.showLogo,
        settings.logoUrl,
        this.companyLogoUrl,
      ),
      qrData: this.previewQrTarget,
      opacity: settings.opacity,
      qrBackgroundColor: settings.qrBackgroundColor ?? "#f5f5f5",
      logoOpacity: settings.logoOpacity ?? 0.92,
      logoBackgroundColor: settings.logoBackgroundColor ?? "#ffffff",
      marginPx: settings.marginPx,
      logoMarginPx: settings.logoMarginPx ?? 24,
      sizeRatio: settings.sizeRatio,
      logoSizeRatio: settings.logoSizeRatio ?? MEET_LAYOUT.logoScale.default,
      fontFamily,
      fontCssUrl,
      companyFontFileUrl: usesCompanyFont ? this.companyFontFileUrl : null,
      loading: this.listPreviewLoading,
      renderedImageUrl: meetEmployeeRenderUrl(
        this.selectedDetail?.employeeRenders,
        this.previewEmployee?.email,
      ),
    };
  }

  get showRenderSyncing(): boolean {
    if (!this.selected) {
      return false;
    }
    if (this.renderSyncing) {
      return true;
    }
    const hasRender = !!meetEmployeeRenderUrl(
      this.selectedDetail?.employeeRenders,
      this.previewEmployee?.email,
    );
    return this.listPreviewLoading && !hasRender;
  }

  get listBusy(): boolean {
    return this.listLoading;
  }

  get contentReady(): boolean {
    if (this.companyLoading || this.showRenderSyncing) {
      return false;
    }
    const preview = this.previewBindings;
    return !!preview && !preview.loading;
  }

  get mainPhase(): CanvasListMainPhase {
    return canvasListMainPhase({
      listBusy: this.listBusy,
      hasSelection: !!this.selected,
      contentReady: this.contentReady,
    });
  }

  get mainReady(): boolean {
    return this.mainPhase === "ready";
  }

  get pickerTemplateContextId(): string {
    return "meet-backgrounds";
  }

  get listPreviewLoading(): boolean {
    return (
      this.loadingPreviewQr ||
      this.previewQrSyncing ||
      this.loadingDetail ||
      this.previewEmployeeLoading
    );
  }

  ngOnDestroy(): void {
    this.storeSub?.unsubscribe();
    this.previewQrSub?.unsubscribe();
    this.renderSyncSub?.unsubscribe();
  }

  select(item: MeetBackground): void {
    const meetBackgroundId = item.meetBackgroundId?.trim();
    if (!meetBackgroundId) {
      return;
    }
    if ((this.selected?.meetBackgroundId ?? "") === meetBackgroundId) {
      return;
    }
    this.selected = item;
    this.selectedDetail = null;
    this.previewQrTarget = null;
    this.previewEmployeeData = null;
    this.stopRenderSync();
    this.refreshPreviewQr(item);
    this.loadSelectedDetail(meetBackgroundId);
    this.previewEmail$.next(
      this.previewEmployee?.email?.trim().toLowerCase() ?? "",
    );
  }

  onPreviewEmployeeChange(employee: EmployeePickerItem | null): void {
    this.previewEmployee = employee;
    this.employeePickerReady = true;
    this.listLoading = this.storeListLoading;
    this.loading = this.listLoading || this.companyLoading;
    this.previewEmail$.next(employee?.email?.trim().toLowerCase() ?? "");
    this.syncEmployeeRenderIfNeeded();
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    this.listLoading = this.storeListLoading;
    this.loading = this.listLoading || this.companyLoading;
    this.previewEmail$.next(
      this.previewEmployee?.email?.trim().toLowerCase() ?? "",
    );
    this.syncEmployeeRenderIfNeeded();
  }

  isSelected(item: MeetBackground): boolean {
    return (
      (this.selected?.meetBackgroundId ?? "") === (item.meetBackgroundId ?? "")
    );
  }

  createNew(): void {
    void this.router.navigate(["/backgrounds/meets/create"]);
  }

  editSelected(): void {
    const meetBackgroundId = this.selected?.meetBackgroundId?.trim();
    if (!meetBackgroundId) {
      return;
    }
    void this.router.navigate(["/backgrounds/meets", meetBackgroundId, "edit"]);
  }

  setAsDefault(item: MeetBackground): void {
    const itemId = item.meetBackgroundId?.trim();
    if (!itemId || item.isDefault || this.defaultSettingTemplateId) {
      return;
    }
    const data: ConfirmationDialogParams = {
      title: "digitalCards.default.setDefaultConfirmation",
      body: "digitalCards.default.setDefaultBody",
      confirmButton: "digitalCards.default.setDefaultButton",
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "400px",
      data,
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.defaultSettingTemplateId = itemId;
      this.meetBackgroundService.setDefault(itemId).subscribe({
        next: (updated) => {
          this.defaultSettingTemplateId = null;
          this.toastr.success(
            this.translate.instant("meetBackground.defaultSet") as string,
          );
          this.store.dispatch(new GetMeetBackgroundsAction());
          this.selected = updated;
        },
        error: () => {
          this.defaultSettingTemplateId = null;
        },
      });
    });
  }

  remove(): void {
    const targetId = this.selected?.meetBackgroundId?.trim();
    if (!targetId || this.deleting) {
      return;
    }
    this.deleting = true;
    this.store.dispatch(new DeleteMeetBackgroundAction(targetId));
    const done = merge(
      this.actions$.pipe(
        ofType(DELETE_MEET_BACKGROUND_SUCCESS),
        filter(
          (action: DeleteMeetBackgroundSuccessAction) =>
            action.payload === targetId,
        ),
      ),
      this.actions$.pipe(ofType(DELETE_MEET_BACKGROUND_FAILURE)),
    )
      .pipe(take(1))
      .subscribe(() => {
        this.deleting = false;
      });
    this.storeSub?.add(done);
  }

  private loadSelectedDetail(meetBackgroundId: string): void {
    if (!meetBackgroundId?.trim()) {
      this.selectedDetail = null;
      this.loadingDetail = false;
      this.stopRenderSync();
      return;
    }
    this.detailRequestSeq += 1;
    const requestSeq = this.detailRequestSeq;
    this.loadingDetail = true;
    this.selectedDetail = null;
    this.stopRenderSync();
    this.meetBackgroundService.getOne(meetBackgroundId).subscribe({
      next: (detail) => {
        if (requestSeq !== this.detailRequestSeq) return;
        this.selectedDetail = detail;
        this.loadingDetail = false;
        this.syncEmployeeRenderIfNeeded();
      },
      error: () => {
        if (requestSeq !== this.detailRequestSeq) return;
        this.selectedDetail = null;
        this.loadingDetail = false;
        this.stopRenderSync();
      },
    });
  }

  private templateExpectsStoredRender(item: MeetBackground | null): boolean {
    if (!item?.settings) {
      return false;
    }
    return (
      !!(item.digitalCardProfileId ?? "").trim() ||
      item.settings.showLogo === true
    );
  }

  private syncEmployeeRenderIfNeeded(): void {
    const meetBackgroundId = this.selected?.meetBackgroundId?.trim();
    const email = this.previewEmployee?.email?.trim().toLowerCase() ?? "";
    if (
      !meetBackgroundId ||
      !email ||
      !this.employeePickerReady ||
      this.loadingDetail ||
      !this.templateExpectsStoredRender(this.selected)
    ) {
      this.stopRenderSync();
      return;
    }
    if (meetEmployeeRenderUrl(this.selectedDetail?.employeeRenders, email)) {
      this.stopRenderSync();
      return;
    }
    this.startRenderSync(meetBackgroundId, email);
  }

  private startRenderSync(meetBackgroundId: string, email: string): void {
    this.renderSyncSub?.unsubscribe();
    this.renderSyncSeq += 1;
    const syncSeq = this.renderSyncSeq;
    let polls = 0;
    this.renderSyncing = true;
    this.renderSyncSub = timer(0, POLLING_INTERVAL)
      .pipe(
        switchMap(() =>
          this.meetBackgroundService
            .getOne(meetBackgroundId)
            .pipe(catchError(() => of<MeetBackground | null>(null))),
        ),
        takeWhile((detail) => {
          if (syncSeq !== this.renderSyncSeq) {
            return false;
          }
          polls += 1;
          if (polls > RENDER_SYNC_MAX_POLLS) {
            return false;
          }
          if (!detail) {
            return true;
          }
          this.selectedDetail = detail;
          return !meetEmployeeRenderUrl(detail.employeeRenders, email);
        }, true),
      )
      .subscribe({
        next: (detail) => {
          if (syncSeq !== this.renderSyncSeq) {
            return;
          }
          if (detail && meetEmployeeRenderUrl(detail.employeeRenders, email)) {
            this.stopRenderSync();
          }
        },
        complete: () => {
          if (syncSeq === this.renderSyncSeq) {
            this.renderSyncing = false;
          }
        },
      });
  }

  private stopRenderSync(): void {
    this.renderSyncSub?.unsubscribe();
    this.renderSyncSub = undefined;
    this.renderSyncSeq += 1;
    this.renderSyncing = false;
  }

  private refreshPreviewQr(item: MeetBackground): void {
    const profileId = item.digitalCardProfileId?.trim() || "";
    this.previewQrSub?.unsubscribe();
    this.previewQrRequestSeq += 1;
    const requestSeq = this.previewQrRequestSeq;
    if (!profileId) {
      this.previewQrTarget = meetFallbackQrTarget();
      this.loadingPreviewQr = false;
      this.previewQrSyncing = false;
      return;
    }
    this.loadingPreviewQr = true;
    this.previewQrSyncing = false;
    this.previewQrSub = this.meetBackgroundService
      .getPreviewQrTarget(profileId)
      .subscribe({
        next: (response) => {
          if (requestSeq !== this.previewQrRequestSeq) return;
          this.previewQrTarget = response.qrTarget || meetFallbackQrTarget();
          this.loadingPreviewQr = false;
          this.previewQrSyncing = false;
        },
        error: () => {
          if (requestSeq !== this.previewQrRequestSeq) return;
          this.loadingPreviewQr = false;
          this.previewQrSyncing = true;
          this.previewQrSub = timer(0, POLLING_INTERVAL)
            .pipe(
              switchMap(() =>
                forkJoin({
                  readiness: this.digitalCardService
                    .getProfileReadiness(profileId, false)
                    .pipe(catchError(() => of({ status: "pending" as const }))),
                  qr: this.meetBackgroundService
                    .getPreviewQrTarget(profileId)
                    .pipe(catchError(() => of(null))),
                }),
              ),
              takeWhile(
                (result) =>
                  result.readiness.status === "pending" && !result.qr?.qrTarget,
                true,
              ),
            )
            .subscribe({
              next: (result) => {
                if (requestSeq !== this.previewQrRequestSeq) return;
                if (result.qr?.qrTarget) {
                  this.previewQrTarget = result.qr.qrTarget;
                  this.previewQrSyncing = false;
                  return;
                }
                if (result.readiness.status === "ready") {
                  this.previewQrTarget = meetFallbackQrTarget();
                  this.previewQrSyncing = false;
                }
              },
              error: () => {
                if (requestSeq !== this.previewQrRequestSeq) return;
                this.previewQrTarget = meetFallbackQrTarget();
                this.previewQrSyncing = false;
              },
            });
        },
      });
  }
}
